import { Fragment, useEffect, useRef, useState } from "react";
import { StaffColor } from "../lib/staff-colors.ts";
import { groupOverlaps } from "../lib/time.ts";
import type { FormatPrefs } from "../lib/i18n.ts";
import { formatTime as fmtTimeI18n } from "../lib/i18n.ts";
import { BlockIcon, CloseIcon } from "./Icon.tsx";
import { CalendarEvent } from "./CalendarEvent.tsx";
import { MobileStaffStrip } from "./MobileStaffStrip.tsx";

interface Appointment {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  service_name: string;
  service_ids?: string;
  staff_id: string;
  staff_name: string;
  notes?: string | null;
  /** 1 if this appointment spans the whole day (renders in the all-day row). */
  is_all_day?: number | boolean;
}

interface BlockedTime {
  id: string;
  staff_id: string;
  start_at: string;
  end_at: string;
  reason?: string;
}

export interface WorkingHourRow {
  staff_id: string;
  day_of_week: number; // 0=Sun..6=Sat
  start_time: string; // "HH:MM"
  end_time: string; // "HH:MM"
  is_active: number; // 1 if working
}

interface StaffMember {
  id: string;
  name: string;
}

interface Props {
  appointments: Appointment[];
  staff: StaffMember[];
  staffColorMap: Map<string, StaffColor>;
  date: string; // YYYY-MM-DD
  blockedTimes?: BlockedTime[];
  /** Per-staff working hours by day_of_week. Used to shade non-working slots. */
  staffHours?: Map<string, WorkingHourRow[]>;
  /** Zoom multiplier on the row height. 1 = 56px/hour, 2 = 112, 4 = 224. */
  zoom?: 1 | 2 | 4;
  /** Locale + 12/24-hour preferences. Defaults to browser-locale 12-hour. */
  formatPrefs?: FormatPrefs;
  /** Per-service accent hex (#RRGGBB) keyed by service id; overrides staff colour on the event card. */
  serviceAccents?: Map<string, string>;
  /** Inline status action — kept for the mobile/list views only. */
  onAction?: (id: string, status: string) => void;
  /** Click handler for events — opens AppointmentDetailsModal. */
  onSelectAppt?: (id: string) => void;
  /** Right-click on event — most parents will open a CalendarContextMenu. */
  onAppointmentRightClick?: (args: { id: string; pageX: number; pageY: number }) => void;
  /**
   * Click or drag-select on empty cell — quick-add. `endTime` is `undefined`
   * for a click, and a `"HH:MM"` string for a drag-select. Snapped to 15 min.
   */
  onEmptyCellClick?: (args: { date: string; time: string; endTime?: string; staffId: string }) => void;
  /** Mobile-only: horizontal swipe across the grid → prev/next day. */
  onSwipeDay?: (delta: -1 | 1) => void;
  /** Drag-reschedule / cross-staff drop / resize. Patch contains the new ISO times + staff. */
  onReschedule?: (id: string, patch: { start_at: string; end_at: string; staff_id: string }) => Promise<void> | void;
  /** Alt+drag → clone the source appointment to a new slot. */
  onDuplicate?: (id: string, patch: { start_at: string; staff_id: string }) => Promise<void> | void;
  /**
   * Right-click on empty cell — fired with the time + staff under the cursor.
   * Most parents will open a context menu offering "New appointment" /
   * "Block this time" with the same prefill as onEmptyCellClick.
   */
  onEmptyCellRightClick?: (args: {
    date: string;
    time: string;
    staffId: string;
    pageX: number;
    pageY: number;
  }) => void;
  onDeleteBlock?: (block: BlockedTime) => void;
}

const BASE_ROW_HEIGHT = 56; // px per hour at zoom = 1
const DEFAULT_HOUR_START = 6;
const DEFAULT_HOUR_END = 22;

/**
 * Derive the visible hour range for the day from staff working_hours,
 * padded ±1 hour. Falls back to 6 AM–10 PM if no hours are configured.
 */
function deriveHourRange(
  staffHours: Map<string, WorkingHourRow[]> | undefined,
  dayOfWeek: number,
): { start: number; end: number } {
  if (!staffHours || staffHours.size === 0) {
    return { start: DEFAULT_HOUR_START, end: DEFAULT_HOUR_END };
  }

  let earliest = Infinity;
  let latest = -Infinity;
  for (const rows of staffHours.values()) {
    for (const r of rows) {
      if (r.day_of_week !== dayOfWeek || !r.is_active) continue;
      const startMin = parseInt(r.start_time.split(":")[0], 10);
      const endMinH = parseInt(r.end_time.split(":")[0], 10);
      const endMinM = parseInt(r.end_time.split(":")[1] ?? "0", 10);
      const endHour = endMinM > 0 ? endMinH + 1 : endMinH;
      if (startMin < earliest) earliest = startMin;
      if (endHour > latest) latest = endHour;
    }
  }

  if (!isFinite(earliest) || !isFinite(latest)) {
    return { start: DEFAULT_HOUR_START, end: DEFAULT_HOUR_END };
  }

  // Pad ±1 hour, clamp to [0, 24].
  return {
    start: Math.max(0, earliest - 1),
    end: Math.min(24, latest + 1),
  };
}

function formatHour(hour: number, prefs?: FormatPrefs): string {
  if (prefs?.hourFormat === "24") return String(hour).padStart(2, "0");
  const suffix = hour >= 12 ? "PM" : "AM";
  const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${h}${suffix}`;
}

function timeToMinutes(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function getTopOffset(startIso: string, hourStart: number, rowHeight: number): number {
  const mins = timeToMinutes(startIso) - hourStart * 60;
  return Math.max(0, (mins / 60) * rowHeight);
}

function getBlockHeight(startIso: string, endIso: string, hourStart: number, hourEnd: number, rowHeight: number): number {
  const startMins = Math.max(timeToMinutes(startIso), hourStart * 60);
  const endMins = Math.min(timeToMinutes(endIso), hourEnd * 60);
  const durationMins = Math.max(endMins - startMins, 15);
  return (durationMins / 60) * rowHeight;
}

/** True when viewport width is ≤ 720px. Updates on viewport resize. */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 720px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const update = () => setMobile(mq.matches);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return mobile;
}

// Tick every 60s so the now-line moves down the day.
function useNowMinutes() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function isSameYMD(date: Date, ymd: string): boolean {
  const [y, m, d] = ymd.split("-").map(Number);
  return date.getFullYear() === y && date.getMonth() + 1 === m && date.getDate() === d;
}

/** True when the duplicate-ghost should render in the given staff column. */
function isDuplicateGhostInColumn(
  drag: { mode: string; moved: boolean; targetStaffId: string } | null,
  staffId: string,
  _appointments: unknown,
): boolean {
  return !!drag && drag.mode === "duplicate" && drag.moved && drag.targetStaffId === staffId;
}

/** Resolve the per-service accent for an appointment, if any. */
function resolveAccent(
  appt: Appointment,
  serviceAccents: Map<string, string> | undefined,
): string | undefined {
  if (!serviceAccents || !appt.service_ids) return undefined;
  try {
    const ids = JSON.parse(appt.service_ids);
    if (Array.isArray(ids) && ids.length > 0 && typeof ids[0] === "string") {
      return serviceAccents.get(ids[0]);
    }
  } catch {
    // service_ids isn't JSON for some reason — silently skip.
  }
  return undefined;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m || 0);
}

/**
 * Compute a list of [topPx, heightPx] ranges to shade as "non-working"
 * for a single staff column on a given day-of-week. Out-of-range hours
 * (before HOUR_START / after HOUR_END) are clipped.
 */
function computeNonWorkingRanges(
  hours: WorkingHourRow[] | undefined,
  dayOfWeek: number,
  hourStart: number,
  hourEnd: number,
  rowHeight: number,
): Array<[number, number]> {
  const dayHours = (hours ?? []).filter((h) => h.day_of_week === dayOfWeek && h.is_active);
  const gridStart = hourStart * 60;
  const gridEnd = hourEnd * 60;

  if (dayHours.length === 0) {
    // Whole day off — shade the full column.
    return [[0, (gridEnd - gridStart) * (rowHeight / 60)]];
  }

  // Sort + collapse overlaps in working windows.
  const windows = dayHours
    .map((h) => [hhmmToMinutes(h.start_time), hhmmToMinutes(h.end_time)] as [number, number])
    .sort((a, b) => a[0] - b[0]);

  // Merge overlapping windows.
  const merged: Array<[number, number]> = [];
  for (const [s, e] of windows) {
    if (merged.length > 0 && s <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    } else {
      merged.push([s, e]);
    }
  }

  // Compute the gaps (non-working intervals) inside the visible grid.
  const gaps: Array<[number, number]> = [];
  let cursor = gridStart;
  for (const [s, e] of merged) {
    if (s > cursor) gaps.push([cursor, Math.min(s, gridEnd)]);
    cursor = Math.max(cursor, e);
    if (cursor >= gridEnd) break;
  }
  if (cursor < gridEnd) gaps.push([cursor, gridEnd]);

  return gaps
    .filter(([s, e]) => e > s)
    .map(([s, e]) => [
      (s - gridStart) * (rowHeight / 60),
      (e - s) * (rowHeight / 60),
    ]);
}

// Desktop: Multi-column staff view
function DesktopView({ appointments, staff, staffColorMap, blockedTimes = [], staffHours, serviceAccents, date, zoom = 1, formatPrefs, onSelectAppt, onAppointmentRightClick, onEmptyCellClick, onReschedule, onDuplicate, onEmptyCellRightClick, onDeleteBlock }: Props) {
  const ROW_HEIGHT = BASE_ROW_HEIGHT * zoom;

  const dayOfWeek = (() => {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d).getDay();
  })();

  const { start: HOUR_START, end: HOUR_END } = deriveHourRange(staffHours, dayOfWeek);
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const totalHeight = (HOUR_END - HOUR_START) * ROW_HEIGHT;

  const now = useNowMinutes();
  const showNowLine = isSameYMD(now, date);
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const nowTop = (nowMins - HOUR_START * 60) * (ROW_HEIGHT / 60);
  const nowInRange = nowMins >= HOUR_START * 60 && nowMins <= HOUR_END * 60;

  // ── Drag-to-reschedule / resize state ──────────────────────────────────────
  const gridRef = useRef<HTMLDivElement>(null);
  const staffColumnRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  type DragState = {
    apptId: string;
    mode: "move" | "resize" | "duplicate";
    startClientX: number;
    startClientY: number;
    originalStartAt: string;
    originalEndAt: string;
    originalStaffId: string;
    /** Signed minutes from cursor — smooth (1-min granularity, no snap).
     *  Drives the live card position so it tracks the cursor without
     *  jumping in 15-min increments. */
    deltaMinRaw: number;
    /** Snapped to 15 min — drives the inline time label and the eventual
     *  commit on pointerup. */
    deltaMin: number;
    targetStaffId: string;
    moved: boolean; // crossed the click-vs-drag threshold
  };
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  // Set when a real drag commits — used by the click-capture handler on the
  // event wrapper to swallow the synthetic click that follows pointerup
  // (otherwise the details modal would open after every drag-reschedule).
  const justDraggedRef = useRef(false);

  function snapMin(min: number): number {
    return Math.round(min / 15) * 15;
  }

  function shiftIso(iso: string, deltaMin: number): string {
    const d = new Date(iso);
    d.setMinutes(d.getMinutes() + deltaMin);
    return d.toISOString();
  }

  function staffIdAtClientX(x: number): string | null {
    for (const [id, el] of staffColumnRefs.current) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right) return id;
    }
    return null;
  }

  function startDrag(args: { apptId: string; mode: "move" | "resize" | "duplicate"; clientX: number; clientY: number }) {
    if (!onReschedule) return;
    const appt = appointments.find((a) => a.id === args.apptId);
    if (!appt) return;
    setDrag({
      apptId: args.apptId,
      mode: args.mode,
      startClientX: args.clientX,
      startClientY: args.clientY,
      originalStartAt: appt.start_at,
      originalEndAt: appt.end_at,
      originalStaffId: appt.staff_id,
      deltaMinRaw: 0,
      deltaMin: 0,
      targetStaffId: appt.staff_id,
      moved: false,
    });
  }

  // Global pointer move/up listeners while a drag is in progress.
  useEffect(() => {
    if (!drag) return;

    function onMove(e: PointerEvent) {
      const cur = dragRef.current;
      if (!cur) return;
      const dy = e.clientY - cur.startClientY;
      const dx = e.clientX - cur.startClientX;
      const deltaMinRaw = (dy / ROW_HEIGHT) * 60;
      const deltaMin = snapMin(deltaMinRaw);
      const targetStaffId =
        cur.mode === "move" ? staffIdAtClientX(e.clientX) ?? cur.originalStaffId : cur.originalStaffId;
      const moved = cur.moved || Math.abs(dx) > 4 || Math.abs(dy) > 4;
      // Update on raw-delta change too so the card follows cursor smoothly.
      if (
        deltaMinRaw !== cur.deltaMinRaw ||
        deltaMin !== cur.deltaMin ||
        targetStaffId !== cur.targetStaffId ||
        moved !== cur.moved
      ) {
        setDrag({ ...cur, deltaMinRaw, deltaMin, targetStaffId, moved });
      }
    }

    function onUp() {
      const cur = dragRef.current;
      setDrag(null);
      if (!cur || !cur.moved) return; // pure click — ignore (CalendarEvent's onClick handles)

      // Mark that we just drag-committed; the click-capture handler on the
      // event wrapper checks this and stops the synthetic click from bubbling
      // into CalendarEvent's onSelect (which would open the details modal).
      justDraggedRef.current = true;
      // Clear on the next task tick so subsequent clicks behave normally.
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 0);

      let newStart = cur.originalStartAt;
      let newEnd = cur.originalEndAt;
      if (cur.mode === "resize") {
        if (cur.deltaMin === 0) return;
        newEnd = shiftIso(cur.originalEndAt, cur.deltaMin);
        if (new Date(newEnd).getTime() <= new Date(newStart).getTime()) {
          newEnd = shiftIso(newStart, 15);
        }
      } else {
        if (cur.deltaMin === 0 && cur.targetStaffId === cur.originalStaffId) return;
        newStart = shiftIso(cur.originalStartAt, cur.deltaMin);
        newEnd = shiftIso(cur.originalEndAt, cur.deltaMin);
      }

      if (cur.mode === "duplicate" && onDuplicate) {
        void onDuplicate(cur.apptId, {
          start_at: newStart,
          staff_id: cur.targetStaffId,
        });
        return;
      }

      if (!onReschedule) return;
      void onReschedule(cur.apptId, {
        start_at: newStart,
        end_at: newEnd,
        staff_id: cur.targetStaffId,
      });
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [drag, appointments, onReschedule, onDuplicate]);

  // Cap per-staff column width so a tenant with 2 staff doesn't get
  // 1000px columns on a wide monitor. minmax(0, 1fr) lets columns
  // shrink freely when many staff need to fit a small viewport;
  // maxWidth caps the grid so few staff don't sprawl across the
  // whole screen — calendar is left-aligned with empty space to
  // the right when the cap kicks in.
  const STAFF_COL_MAX = 360;
  const TIME_GUTTER = 48;
  const gridMaxWidth = TIME_GUTTER + (staff.length || 1) * STAFF_COL_MAX;

  return (
    <div
      ref={gridRef}
      style={{
        display: "grid",
        gridTemplateColumns: `${TIME_GUTTER}px repeat(${staff.length || 1}, minmax(0, 1fr))`,
        maxWidth: `${gridMaxWidth}px`,
        borderRadius: 14,
        overflow: "hidden",
        background: "var(--paper-raised)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      {/* Header row */}
      {/* Empty corner */}
      <div
        style={{
          borderBottom: "1px solid var(--border)",
          borderRight: "1px solid var(--border)",
          background: "var(--bg-secondary)",
        }}
      />
      {/* Staff headers */}
      {staff.map((s) => {
        const color = staffColorMap.get(s.id);
        const initial = s.name.charAt(0).toUpperCase();
        return (
          <div
            key={s.id}
            style={{
              borderBottom: "1px solid var(--border)",
              borderRight: "1px solid var(--border)",
              padding: "0.5rem 0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "var(--bg-secondary)",
            }}
          >
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                background: color?.badge ?? "var(--accent-light)",
                color: color?.badgeText ?? "var(--accent-text)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initial}
            </div>
            <span
              style={{
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "var(--text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {s.name}
            </span>
          </div>
        );
      })}

      {/* All-day row — only renders if there's at least one all-day event today */}
      {appointments.some((a) => !!a.is_all_day) && (
        <>
          <div
            style={{
              borderBottom: "1px solid var(--border)",
              borderRight: "1px solid var(--border)",
              background: "var(--paper-well)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9.5,
              color: "var(--ink-faint)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontWeight: 500,
              minHeight: 32,
            }}
          >
            All day
          </div>
          {staff.map((s) => {
            const color = staffColorMap.get(s.id);
            const items = appointments.filter((a) => a.staff_id === s.id && !!a.is_all_day);
            return (
              <div
                key={`allday-${s.id}`}
                style={{
                  borderBottom: "1px solid var(--border)",
                  borderRight: "1px solid var(--border)",
                  background: "var(--paper-well)",
                  padding: "4px 6px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  minHeight: 32,
                }}
              >
                {items.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onSelectAppt?.(a.id)}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: color?.bg ?? "var(--accent-soft)",
                      color: color?.text ?? "var(--accent-ink)",
                      border: `1px solid ${color?.border ?? "var(--accent)"}`,
                      cursor: onSelectAppt ? "pointer" : "default",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {a.customer_name}
                  </button>
                ))}
              </div>
            );
          })}
        </>
      )}

      {/* Time rows */}
      {/* Time gutter */}
      <div style={{ position: "relative", height: `${totalHeight}px`, borderRight: "1px solid var(--border)" }}>
        {hours.map((hour) => (
          <div
            key={hour}
            style={{
              position: "absolute",
              top: `${(hour - HOUR_START) * ROW_HEIGHT}px`,
              width: "100%",
              height: `${ROW_HEIGHT}px`,
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: "4px",
            }}
          >
            <span style={{ fontSize: "0.625rem", color: "var(--text-muted)", fontWeight: 500 }}>
              {formatHour(hour, formatPrefs)}
            </span>
          </div>
        ))}
        {/* Sub-hour labels in the gutter — same tiering as the gridlines.
            zoom 1×: none. zoom 2×: :30. zoom 4×: :15, :30, :45. */}
        {(() => {
          const subMinutes: number[] =
            zoom >= 4 ? [15, 30, 45] : zoom >= 2 ? [30] : [];
          return hours.map((hour) =>
            subMinutes.map((mins) => (
              <span
                key={`gutter-${hour}-${mins}`}
                aria-hidden
                style={{
                  position: "absolute",
                  top: `${(hour - HOUR_START) * ROW_HEIGHT + (mins / 60) * ROW_HEIGHT - 5}px`,
                  width: "100%",
                  textAlign: "center",
                  fontSize: "0.5625rem",
                  color: "var(--ink-faint)",
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "0.02em",
                  pointerEvents: "none",
                }}
              >
                :{String(mins).padStart(2, "0")}
              </span>
            )),
          );
        })()}
        {/* Now-line dot in the gutter (only on today) */}
        {showNowLine && nowInRange && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: `${nowTop - 4}px`,
              right: -4,
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#ef4444",
              zIndex: 3,
            }}
          />
        )}
      </div>

      {/* Staff columns */}
      {staff.map((s) => {
        // Split: all-day events render in the all-day row at the top of the
        // staff header; timed events render in the hour grid.
        const allOf = appointments.filter((a) => a.staff_id === s.id);
        const colAppts = allOf.filter((a) => !a.is_all_day);
        const colBlocks = blockedTimes.filter((b) => b.staff_id === s.id);
        const color = staffColorMap.get(s.id);

        return (
          <StaffColumn
            key={s.id}
            staffId={s.id}
            totalHeight={totalHeight}
            hourStart={HOUR_START}
            rowHeight={ROW_HEIGHT}
            date={date}
            formatPrefs={formatPrefs}
            zoom={zoom}
            onEmptyCellClick={onEmptyCellClick}
            onEmptyCellRightClick={onEmptyCellRightClick}
            // Suppress the hover-preview while an event is being dragged —
            // otherwise the cursor's slot suggests the wrong destination.
            suppressHover={!!drag}
            innerRef={(el) => {
              if (el) staffColumnRefs.current.set(s.id, el);
              else staffColumnRefs.current.delete(s.id);
            }}
          >
            {/* Non-working shading for this staff member */}
            {computeNonWorkingRanges(staffHours?.get(s.id), dayOfWeek, HOUR_START, HOUR_END, ROW_HEIGHT).map(([top, height], i) => (
              <div
                key={`nw-${i}`}
                aria-hidden
                style={{
                  position: "absolute",
                  top: `${top}px`,
                  left: 0,
                  right: 0,
                  height: `${height}px`,
                  background: "color-mix(in oklab, var(--ink) 4%, transparent)",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
            ))}

            {/* Hour grid lines */}
            {hours.map((hour) => (
              <div
                key={hour}
                style={{
                  position: "absolute",
                  top: `${(hour - HOUR_START) * ROW_HEIGHT}px`,
                  left: 0,
                  right: 0,
                  height: `${ROW_HEIGHT}px`,
                  borderBottom: "1px solid var(--border)",
                }}
              />
            ))}

            {/* Sub-hour tick lines, scaled by zoom level. Each tier is
                rendered at a slightly stronger opacity so the half-hour
                stands out more than the 15-min, etc. Visible:
                  zoom 1× → 30 min only
                  zoom 2× → 30 + 15 min
                  zoom 4× → 30 + 15 + 5 min                                  */}
            {(() => {
              const tickMinutes: number[] =
                zoom >= 4
                  ? [
                      5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55,
                    ]
                  : zoom >= 2
                    ? [15, 30, 45]
                    : [30];
              const opacityFor = (m: number): string => {
                if (m === 30) return "color-mix(in oklab, var(--ink) 5%, transparent)";
                if (m % 15 === 0) return "color-mix(in oklab, var(--ink) 3%, transparent)";
                return "color-mix(in oklab, var(--ink) 2%, transparent)"; // 5-min ticks
              };
              return hours.map((hour) =>
                tickMinutes.map((mins) => (
                  <div
                    key={`tick-${hour}-${mins}`}
                    aria-hidden
                    style={{
                      position: "absolute",
                      top: `${(hour - HOUR_START) * ROW_HEIGHT + (mins / 60) * ROW_HEIGHT}px`,
                      left: 0,
                      right: 0,
                      height: 1,
                      background: opacityFor(mins),
                      pointerEvents: "none",
                    }}
                  />
                )),
              );
            })()}

            {/* Now-line (only on today, only inside operating hours) */}
            {showNowLine && nowInRange && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: `${nowTop}px`,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: "#ef4444",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Blocked-time overlays (striped) */}
            {colBlocks.map((block) => {
              const top = getTopOffset(block.start_at, HOUR_START, ROW_HEIGHT);
              const height = getBlockHeight(block.start_at, block.end_at, HOUR_START, HOUR_END, ROW_HEIGHT);
              return (
                <div
                  key={block.id}
                  onClick={(e) => e.preventDefault()}
                  title={block.reason ? `Blocked: ${block.reason}` : "Blocked"}
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    left: "4px",
                    right: "4px",
                    height: `${height}px`,
                    background:
                      "repeating-linear-gradient(135deg, color-mix(in oklab, var(--ink) 8%, transparent) 0 6px, transparent 6px 12px)",
                    border: "1px dashed var(--ink-faint)",
                    borderRadius: 8,
                    zIndex: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "4px 8px",
                    color: "var(--ink-soft)",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <BlockIcon size={12} /> {block.reason ?? "Blocked"}
                  </span>
                  {onDeleteBlock && (
                    <button
                      onClick={() => onDeleteBlock(block)}
                      aria-label="Remove block"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--ink-faint)",
                        cursor: "pointer",
                        padding: 2,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <CloseIcon size={12} />
                    </button>
                  )}
                </div>
              );
            })}

            {/* Appointment blocks (lane-packed for side-by-side overlap) */}
            {(() => {
              const lanes = groupOverlaps(colAppts);
              return colAppts.map((appt) => {
                const isDragSource = drag?.apptId === appt.id;
                // For "move" drag, suppress this card if it's been dropped on another column.
                // For "duplicate", always render the source in place AND render a ghost in target.
                const renderHere =
                  !isDragSource ||
                  drag?.mode === "resize" ||
                  drag?.mode === "duplicate" ||
                  drag?.targetStaffId === s.id;
                if (!renderHere) return null;

                const isMoving = isDragSource && drag.mode === "move" && drag.moved;
                const isResizing = isDragSource && drag.mode === "resize" && drag.moved;

                // Position uses RAW delta so the card tracks the cursor at
                // 1-min resolution. Inline label uses SNAPPED delta so the
                // operator sees the eventual time. On commit, snap wins.
                const positionStart = isMoving
                  ? shiftIso(appt.start_at, drag.deltaMinRaw)
                  : appt.start_at;
                const positionEnd =
                  isMoving
                    ? shiftIso(appt.end_at, drag.deltaMinRaw)
                    : isResizing
                      ? shiftIso(appt.end_at, drag.deltaMinRaw)
                      : appt.end_at;
                const labelStart = isMoving
                  ? shiftIso(appt.start_at, drag.deltaMin)
                  : appt.start_at;
                const labelEnd =
                  isMoving
                    ? shiftIso(appt.end_at, drag.deltaMin)
                    : isResizing
                      ? shiftIso(appt.end_at, drag.deltaMin)
                      : appt.end_at;

                const top = getTopOffset(positionStart, HOUR_START, ROW_HEIGHT);
                const height = getBlockHeight(positionStart, positionEnd, HOUR_START, HOUR_END, ROW_HEIGHT);
                // Where the card will snap to on release. Rendered as a
                // dashed ghost so the operator sees the eventual landing.
                const snapTop = isMoving || isResizing ? getTopOffset(labelStart, HOUR_START, ROW_HEIGHT) : top;
                const snapHeight = isMoving || isResizing ? getBlockHeight(labelStart, labelEnd, HOUR_START, HOUR_END, ROW_HEIGHT) : height;
                const lane = lanes.get(appt.id) ?? { columnIndex: 0, columnCount: 1 };
                const widthPct = 100 / lane.columnCount;
                const leftPct = lane.columnIndex * widthPct;

                const onPointerDownEvent = onReschedule
                  ? (e: React.PointerEvent<HTMLDivElement>) => {
                      if (e.button !== 0) return;
                      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      const fromBottom = rect.bottom - e.clientY;
                      // Resize handle takes priority over alt-key (resizing a
                      // duplicate doesn't make sense in v1). Hit zone scales
                      // with the rendered handle height (8px mouse / 12px touch).
                      const handleHit = e.pointerType === "touch" ? 12 : 8;
                      const isResize = fromBottom <= handleHit;
                      const isAlt = e.altKey && !isResize && !!onDuplicate;
                      const mode: "move" | "resize" | "duplicate" = isResize
                        ? "resize"
                        : isAlt
                          ? "duplicate"
                          : "move";

                      if (e.pointerType === "touch" || e.pointerType === "pen") {
                        // Touch: a tap must NEVER move an event. Arm the drag only
                        // after a deliberate long-press (~300ms hold, <10px move) —
                        // a quick tap falls through to CalendarEvent's onClick (open
                        // details); a continued hold (~500ms, still in place) opens
                        // the context menu. We don't preventDefault here so the tap's
                        // synthetic click still reaches the card.
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const pageX = e.pageX;
                        const pageY = e.pageY;
                        let armTimer = 0;
                        let menuTimer = 0;
                        const onPreMove = (ev: PointerEvent) => {
                          if (Math.abs(ev.clientX - startX) > 10 || Math.abs(ev.clientY - startY) > 10) {
                            teardown(); // moved before arming → scroll/swipe, not a drag
                          }
                        };
                        const onPreEnd = () => teardown();
                        function teardown() {
                          window.clearTimeout(armTimer);
                          window.clearTimeout(menuTimer);
                          window.removeEventListener("pointermove", onPreMove);
                          window.removeEventListener("pointerup", onPreEnd);
                          window.removeEventListener("pointercancel", onPreEnd);
                        }
                        armTimer = window.setTimeout(() => {
                          // Long-press held → lift the card into a drag.
                          window.removeEventListener("pointermove", onPreMove);
                          try { navigator.vibrate?.(10); } catch { /* no haptics */ }
                          startDrag({ apptId: appt.id, mode, clientX: startX, clientY: startY });
                        }, 300);
                        if (onAppointmentRightClick) {
                          menuTimer = window.setTimeout(() => {
                            const cur = dragRef.current;
                            if (cur && cur.apptId === appt.id && !cur.moved) {
                              setDrag(null);
                              onAppointmentRightClick({ id: appt.id, pageX, pageY });
                              teardown();
                            }
                          }, 500);
                        }
                        window.addEventListener("pointermove", onPreMove);
                        window.addEventListener("pointerup", onPreEnd);
                        window.addEventListener("pointercancel", onPreEnd);
                        return;
                      }

                      // Mouse: immediate drag with the 4px move threshold (unchanged).
                      e.preventDefault();
                      e.stopPropagation();
                      startDrag({
                        apptId: appt.id,
                        mode,
                        clientX: e.clientX,
                        clientY: e.clientY,
                      });
                    }
                  : undefined;

                return (
                  <Fragment key={appt.id}>
                  {/* Snap-target ghost — only while dragging this card.
                      Tells the operator where the card will land on release. */}
                  {(isMoving || isResizing) && (
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        top: `${snapTop}px`,
                        left: `calc(${leftPct}% + 4px)`,
                        width: `calc(${widthPct}% - 8px - ${(lane.columnCount - 1) * 2 / lane.columnCount}px)`,
                        height: `${snapHeight}px`,
                        border: "2px dashed var(--accent)",
                        borderRadius: 8,
                        background: "color-mix(in oklab, var(--accent) 6%, transparent)",
                        pointerEvents: "none",
                        zIndex: 4,
                      }}
                    />
                  )}
                  <div
                    // Capture phase fires BEFORE the inner CalendarEvent.onClick.
                    // If we just finished a drag, swallow the click so the
                    // details modal doesn't open right after a reschedule.
                    onClickCapture={(e) => {
                      if (justDraggedRef.current) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    onClick={(e) => {
                      // Always stop the click from bubbling to the column's
                      // empty-cell handler.
                      e.preventDefault();
                    }}
                    onContextMenu={
                      onAppointmentRightClick
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onAppointmentRightClick({
                              id: appt.id,
                              pageX: e.pageX,
                              pageY: e.pageY,
                            });
                          }
                        : undefined
                    }
                    onPointerDown={onPointerDownEvent}
                    style={{
                      position: "absolute",
                      top: `${top}px`,
                      // 4px outer gutter, plus 2px inter-lane gap
                      left: `calc(${leftPct}% + 4px)`,
                      width: `calc(${widthPct}% - 8px - ${(lane.columnCount - 1) * 2 / lane.columnCount}px)`,
                      height: `${height}px`,
                      zIndex: isMoving || isResizing ? 5 : 1,
                      // Stay fully opaque so the card is easy to track. A
                      // raised shadow is enough visual cue for "this is
                      // being dragged".
                      opacity: 1,
                      boxShadow: isMoving || isResizing ? "0 12px 28px rgba(0,0,0,0.22)" : undefined,
                      transition: drag ? undefined : "top 120ms ease, height 120ms ease",
                    }}
                  >
                    <CalendarEvent
                      appt={
                        // During drag, label shows the SNAPPED eventual time
                        // (so the operator sees what they'll get on release),
                        // while the wrapper position is unsnapped (smooth).
                        isMoving || isResizing
                          ? { ...appt, start_at: labelStart, end_at: labelEnd }
                          : appt
                      }
                      height={height}
                      color={color}
                      accentHex={resolveAccent(appt, serviceAccents)}
                      onSelect={drag?.moved ? undefined : onSelectAppt}
                      showResizeHandle={!!onReschedule}
                      formatPrefs={formatPrefs}
                    />
                  </div>
                  </Fragment>
                );
              });
            })()}

            {/* Duplicate-ghost: shows where the alt-drag clone will land */}
            {isDuplicateGhostInColumn(drag, s.id, appointments) && (() => {
              const src = appointments.find((a) => a.id === drag!.apptId);
              if (!src) return null;
              const newStart = shiftIso(src.start_at, drag!.deltaMin);
              const newEnd = shiftIso(src.end_at, drag!.deltaMin);
              const top = getTopOffset(newStart, HOUR_START, ROW_HEIGHT);
              const height = getBlockHeight(newStart, newEnd, HOUR_START, HOUR_END, ROW_HEIGHT);
              return (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    left: 4,
                    right: 4,
                    height: `${height}px`,
                    background: "color-mix(in oklab, var(--accent) 18%, transparent)",
                    border: "2px dashed var(--accent)",
                    borderRadius: 8,
                    pointerEvents: "none",
                    zIndex: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "var(--accent-ink)",
                  }}
                >
                  Copy
                </div>
              );
            })()}
          </StaffColumn>
        );
      })}
    </div>
  );
}

/**
 * Staff column wrapper that handles pointer events: click for quick-add,
 * click-and-drag for drag-to-create with a translucent ghost block.
 *
 * Children are the absolutely-positioned event/block layers; this component
 * owns the column's own positioning and pointer plumbing.
 */
function StaffColumn({
  staffId,
  totalHeight,
  hourStart,
  rowHeight,
  date,
  formatPrefs,
  zoom,
  onEmptyCellClick,
  onEmptyCellRightClick,
  suppressHover,
  innerRef,
  children,
}: {
  staffId: string;
  totalHeight: number;
  hourStart: number;
  rowHeight: number;
  date: string;
  formatPrefs?: FormatPrefs;
  zoom?: 1 | 2 | 4;
  onEmptyCellClick?: Props["onEmptyCellClick"];
  onEmptyCellRightClick?: Props["onEmptyCellRightClick"];
  /** True when an external drag (event reschedule/resize) is in progress —
   *  hover preview should not render so it doesn't mislead the user. */
  suppressHover?: boolean;
  innerRef?: (el: HTMLDivElement | null) => void;
  children: React.ReactNode;
}) {
  const [drag, setDrag] = useState<{ startY: number; currentY: number } | null>(null);
  const [hoverY, setHoverY] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);
  const setRef = (el: HTMLDivElement | null) => {
    ref.current = el;
    if (innerRef) innerRef(el);
  };

  /** Snap a Y-coord to the nearest slot boundary (in px). Granularity is
   *  15 min by default; at zoom=4 (which renders 5-min ticks) it tightens
   *  to 5 min so the cursor lines up with what the operator can see. */
  const snapMinutes = zoom === 4 ? 5 : 15;
  function snapY(y: number): number {
    const slotPx = (rowHeight * snapMinutes) / 60;
    return Math.max(0, Math.round(y / slotPx) * slotPx);
  }

  function yToTimeStr(y: number): string {
    const totalMin = (y / rowHeight) * 60 + hourStart * 60;
    const snapped = Math.max(0, Math.round(totalMin / snapMinutes) * snapMinutes);
    const hh = String(Math.floor(snapped / 60)).padStart(2, "0");
    const mm = String(snapped % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  }

  const onPointerDown = onEmptyCellClick
    ? (e: React.PointerEvent<HTMLDivElement>) => {
        // Only respond to primary button (left-click / single touch)
        if (e.button !== 0) return;
        // Skip if pointerdown started on an event/block
        if (e.defaultPrevented) return;
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const y = e.clientY - rect.top;
        setDrag({ startY: y, currentY: y });
        // Capture so we keep getting pointermove even if cursor leaves the column
        ref.current.setPointerCapture(e.pointerId);

        // Long-press → fire onEmptyCellRightClick (touch users only).
        longPressFired.current = false;
        if (
          onEmptyCellRightClick &&
          (e.pointerType === "touch" || e.pointerType === "pen") &&
          ref.current
        ) {
          const pageX = e.pageX;
          const pageY = e.pageY;
          const time = yToTimeStr(y);
          longPressTimer.current = window.setTimeout(() => {
            longPressTimer.current = null;
            longPressFired.current = true;
            // Cancel the in-progress drag so the click-vs-drag path is skipped on up.
            setDrag(null);
            onEmptyCellRightClick({ date, time, staffId, pageX, pageY });
          }, 500);
        }
      }
    : undefined;

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const newY = e.clientY - rect.top;
    if (drag) {
      // Cancel pending long-press if movement exceeds tolerance.
      if (longPressTimer.current !== null && Math.abs(newY - drag.startY) > 8) {
        window.clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      setDrag({ ...drag, currentY: newY });
    } else if (e.pointerType === "mouse") {
      // Idle hover: show preview block at the snapped slot.
      setHoverY(newY);
    }
  };

  const onPointerLeave = () => setHoverY(null);

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    // Cancel any pending long-press timer.
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // If long-press already fired, swallow the click/drag.
    if (longPressFired.current) {
      longPressFired.current = false;
      setDrag(null);
      if (ref.current) ref.current.releasePointerCapture(e.pointerId);
      return;
    }
    if (!drag || !onEmptyCellClick || !ref.current) return;
    ref.current.releasePointerCapture(e.pointerId);

    const startY = Math.min(drag.startY, drag.currentY);
    const endY = Math.max(drag.startY, drag.currentY);
    const distancePx = endY - startY;
    setDrag(null);

    const startMin = (startY / rowHeight) * 60;
    const endMin = (endY / rowHeight) * 60;
    const draggedMin = endMin - startMin;

    if (draggedMin < 15 || distancePx < 8) {
      // Treat as click — single point
      onEmptyCellClick({ date, time: yToTimeStr(drag.startY), staffId });
    } else {
      onEmptyCellClick({
        date,
        time: yToTimeStr(startY),
        endTime: yToTimeStr(endY),
        staffId,
      });
    }
  };

  // Render the drag-ghost overlay
  const ghost =
    drag && Math.abs(drag.currentY - drag.startY) > 4 ? (
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 4,
          right: 4,
          top: Math.min(drag.startY, drag.currentY),
          height: Math.abs(drag.currentY - drag.startY),
          background: "color-mix(in oklab, var(--accent) 18%, transparent)",
          border: "1px dashed var(--accent)",
          borderRadius: 8,
          pointerEvents: "none",
          zIndex: 4,
        }}
      />
    ) : null;

  const onContextMenu = onEmptyCellRightClick
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.defaultPrevented) return;
        if (!ref.current) return;
        e.preventDefault();
        const rect = ref.current.getBoundingClientRect();
        const y = e.clientY - rect.top;
        onEmptyCellRightClick({
          date,
          time: yToTimeStr(y),
          staffId,
          pageX: e.pageX,
          pageY: e.pageY,
        });
      }
    : undefined;

  // Render hover preview — only when no drag is active and only on mouse.
  // Two parts: a faint 15-min block (the slot the cursor will pick) AND a
  // thin accent line at the snap row (more prominent than ambient ticks).
  // Label uses formatPrefs so 12h users see "2:00 PM" not "14:00".
  const renderHoverLabel = (hh: string, mm: string): string => {
    if (!formatPrefs) return `${hh}:${mm}`;
    const d = new Date();
    d.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
    return fmtTimeI18n(d, formatPrefs);
  };
  const hoverPreview =
    onEmptyCellClick && hoverY !== null && !drag && !suppressHover ? (
      <>
        {/* Snap-row line — slightly stronger than the ambient sub-hour ticks
            so the cursor's snap target is unambiguous. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: snapY(hoverY),
            height: 1,
            background: "color-mix(in oklab, var(--accent) 50%, transparent)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 4,
            right: 4,
            top: snapY(hoverY),
            // Block height matches snap granularity (15 min by default,
            // 5 min at zoom=4) so it visually equals what's about to be
            // selected.
            height: (rowHeight * snapMinutes) / 60,
            background: "color-mix(in oklab, var(--accent) 8%, transparent)",
            border: "1px dashed color-mix(in oklab, var(--accent) 50%, transparent)",
            borderRadius: 6,
            pointerEvents: "none",
            zIndex: 0,
            display: "flex",
            alignItems: "center",
            paddingLeft: 6,
            fontSize: 10,
            color: "var(--accent-ink)",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "0.04em",
          }}
        >
          {(() => {
            const hhmm = yToTimeStr(snapY(hoverY));
            return renderHoverLabel(hhmm.slice(0, 2), hhmm.slice(3, 5));
          })()}
        </div>
      </>
    ) : null;

  return (
    <div
      ref={setRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onContextMenu={onContextMenu}
      style={{
        position: "relative",
        height: `${totalHeight}px`,
        borderRight: "1px solid var(--border)",
        cursor: onEmptyCellClick ? "copy" : "default",
        touchAction: "none",
      }}
    >
      {hoverPreview}
      {children}
      {ghost}
    </div>
  );
}

export function MultiResourceCalendar(props: Props) {
  const mobile = useIsMobile();
  // Active staff for mobile single-column view. Defaults to the first
  // staff member; persists in component state so swiping between
  // staff doesn't reset on re-renders.
  const [activeStaffId, setActiveStaffId] = useState<string | null>(null);

  // Horizontal-swipe-to-navigate-day on mobile. Tracked at the wrapper
  // level so it doesn't interfere with vertical drag/create/resize
  // inside the grid. Threshold: 80px horizontal travel with horizontal
  // dominating vertical at least 2:1.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const swipeFired = useRef(false);

  if (mobile && props.staff.length > 0) {
    const active = props.staff.find((s) => s.id === activeStaffId) ?? props.staff[0];
    // Per-staff today-count for the strip badge.
    const todayCounts = new Map<string, number>();
    for (const a of props.appointments) {
      todayCounts.set(a.staff_id, (todayCounts.get(a.staff_id) ?? 0) + 1);
    }

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;
      swipeStart.current = { x: e.clientX, y: e.clientY };
      swipeFired.current = false;
    };
    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!swipeStart.current || swipeFired.current || !props.onSwipeDay) return;
      const dx = e.clientX - swipeStart.current.x;
      const dy = e.clientY - swipeStart.current.y;
      if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy) * 2) {
        swipeFired.current = true;
        props.onSwipeDay(dx < 0 ? 1 : -1);
        swipeStart.current = null;
      }
    };
    const onPointerUp = () => {
      swipeStart.current = null;
    };

    return (
      <div onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <MobileStaffStrip
          staff={props.staff}
          activeId={active.id}
          staffColorMap={props.staffColorMap}
          onChange={setActiveStaffId}
          todayCounts={todayCounts}
        />
        <DesktopView
          {...props}
          staff={[active]}
          appointments={props.appointments.filter((a) => a.staff_id === active.id)}
          blockedTimes={props.blockedTimes?.filter((b) => b.staff_id === active.id)}
        />
      </div>
    );
  }

  return <DesktopView {...props} />;
}
